import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * The organisation's Tally connection, polled for everything that shows it:
 * the sync pill in the top bar, Home, and the Tally connection page.
 *
 * Polls every 30 s, or every 5 s while something is moving (first load
 * running, or the owner is waiting for an agent / company to appear).
 */
const TallyContext = createContext(null);

const SLOW_MS = 30_000;
const FAST_MS = 5_000;

export function TallyProvider({ children }) {
  const [state, setState] = useState({ loading: true, company: null, agents: [], error: null });
  const [fast, setFast] = useState(false);
  const timer = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/tally/connection");
      setState({ loading: false, company: data.company, agents: data.agents || [], error: null });
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e }));
    }
  }, []);

  const loadRunning = state.company && state.company.initial_load?.status !== "DONE";

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    clearInterval(timer.current);
    timer.current = setInterval(refresh, fast || loadRunning ? FAST_MS : SLOW_MS);
    return () => clearInterval(timer.current);
  }, [refresh, fast, loadRunning]);

  return (
    <TallyContext.Provider value={{ ...state, refresh, setFastPolling: setFast }}>
      {children}
    </TallyContext.Provider>
  );
}

export function useTally() {
  return useContext(TallyContext);
}

/** One word for where the sync stands, used by the pill and Home. */
export function syncState(company) {
  if (!company) return "NOT_CONNECTED";
  if (!company.agent_online) return "AGENT_OFFLINE";
  if (company.in_tally === false) return "COMPANY_CLOSED";
  if (company.initial_load?.status !== "DONE") return "LOADING";
  return "SYNCED";
}
